// src/components/EmailTemplate.jsx - Professional Email Template with Image Icons
// This file ONLY exports a function that returns HTML strings for emails
// No React hooks or JSX - pure HTML string generation for email clients

/**
 * Generate a professional HTML email template for verification codes
 *
 * @param {Object} params - Template parameters
 * @param {string} params.code - 6-digit verification code
 * @param {string} params.title - Email title (default: 'Admin Login Verification')
 * @param {string} params.description - Email description text
 * @param {number} params.expiresInMinutes - Code expiry time in minutes (default: 10)
 * @param {string} params.type - Verification type ('login' or 'password')
 * @param {string} params.logoUrl - URL of the brand logo
 * @returns {string} Complete HTML email template
 */
export const generateEmailHTML = ({
  code,
  title = 'Admin Login Verification',
  description = 'You are attempting to sign in to the WD Hejbul admin dashboard. Please use the verification code below to complete your login.',
  expiresInMinutes = 10,
  type = 'login',
  logoUrl = 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1778039977/Fabicon-icon-of-wdhejbul_zrboh7.jpg',
}) => {
  // Determine if this is a password reset email
  const isPasswordReset = type === 'password';
  const displayTitle = isPasswordReset ? 'Password Reset Verification' : title;
  const displayDescription = isPasswordReset
    ? 'You have requested to reset your admin password. Please use the verification code below to confirm this action.'
    : description;

  /**
   * Main social media icons configuration
   * Each icon includes: name, URL, image source, and brand color
   */
  const mainSocialIcons = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600198/facebook_kqu1mr.png',
      brandColor: '#1877F2',
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/wdhejbul/',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600198/instagram_zwumhb.png',
      brandColor: '#E4405F',
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600198/linkedin_ghtup3.png',
      brandColor: '#0A66C2',
    },
    {
      name: 'Twitter',
      href: 'https://x.com/wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600198/twitter_frk4kg.png',
      brandColor: '#000000',
    },
    {
      name: 'GitHub',
      href: 'https://github.com/wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600198/icons8-github-500_nytjy1.png',
      brandColor: '#181717',
    },
    {
      name: 'Behance',
      href: 'https://www.behance.net/wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600608/free-behance-icon-106-thumb_clnoco.png',
      brandColor: '#1769FF',
    },
    {
      name: 'Pinterest',
      href: 'https://www.pinterest.com/wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600657/pinterest-icon-black-2_lqoobd.png',
      brandColor: '#E60023',
    },
  ];

  /**
   * Freelance platform icons configuration
   * Displayed in a separate section with WE logo styling
   */
  const freelanceIcons = [
    {
      name: 'Fiverr',
      href: 'https://www.fiverr.com/wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783603438/Fiverr-logo_mtgtee.png',
      brandColor: '#1DBF73',
    },
    {
      name: 'Upwork',
      href: 'https://www.upwork.com/freelancers/~01wdhejbul',
      icon: 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1783600199/upwork-logo-png-transparent_bppmfy.png',
      brandColor: '#6FDA44',
    },
  ];

  /**
   * Footer navigation links
   */
  const footerLinks = [
    { label: 'Home', href: 'https://wdhejbul.com' },
    { label: 'Portfolio', href: 'https://wdhejbul.com/portfolio' },
    { label: 'Services', href: 'https://wdhejbul.com/services' },
    { label: 'Blog', href: 'https://wdhejbul.com/blog' },
    { label: 'About', href: 'https://wdhejbul.com/about' },
    { label: 'Contact', href: 'https://wdhejbul.com/contact' },
  ];

  const year = new Date().getFullYear();

  // =============================================
  // HTML BUILDING HELPERS
  // =============================================

  /**
   * Build a centered table row of social icon cells
   * Uses table-based layout for email client compatibility
   *
   * @param {Array} icons - Array of icon objects
   * @returns {string} HTML string for the icon table
   */
  const buildIconTable = icons => `
    <table role="presentation" align="center" border="0" cellpadding="0" cellspacing="0" style="margin: 0 auto;">
      <tr>
        ${icons
          .map(
            icon => `
          <td style="padding: 0 5px;" align="center" valign="middle">
            <a href="${icon.href}" target="_blank" style="display: inline-block; width: 42px; height: 42px; line-height: 42px; border-radius: 50%; background-color: #e8e8ea; text-align: center; text-decoration: none;" aria-label="${icon.name}" title="${icon.name}">
              <img src="${icon.icon}" width="20" height="20" alt="${icon.name}" style="vertical-align: middle; width: 20px; height: 20px; object-fit: contain; border: 0;" />
            </a>
          </td>`,
          )
          .join('')}
      </tr>
    </table>
  `;

  /**
   * Build a WE logo styled section for freelance platforms
   * Displays icons in card-like containers
   *
   * @returns {string} HTML string for the freelance section
   */
  const buildFreelanceSection = () => {
    const freelanceItems = freelanceIcons
      .map(
        icon => `
      <td style="padding: 0 8px;" align="center" valign="middle">
        <a href="${icon.href}" target="_blank" style="display: inline-block; width: 60px; height: 48px; background: #ffffff; border-radius: 8px; border: 1px solid #e8e8ea; text-align: center; text-decoration: none; padding: 8px 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);" aria-label="${icon.name}" title="${icon.name}">
          <img src="${icon.icon}" width="36" height="28" alt="${icon.name}" style="vertical-align: middle; width: 36px; height: 28px; object-fit: contain; border: 0; display: block; margin: 0 auto;" />
        </a>
      </td>
    `,
      )
      .join('');

    return `
      <table role="presentation" align="center" border="0" cellpadding="0" cellspacing="0" style="margin: 0 auto;">
        <tr>
          ${freelanceItems}
        </tr>
      </table>
    `;
  };

  // =============================================
  // MAIN HTML TEMPLATE
  // =============================================

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${displayTitle}</title>
  <style>
    /* Reset and base styles */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background-color: #f0f0f2;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      padding: 0;
      margin: 0;
      line-height: 1.6;
      min-height: 100vh;
    }
    .email-wrapper {
      max-width: 600px;
      width: 100%;
      margin: 0 auto;
      padding: 0;
      background-color: #f0f0f2;
      min-height: 100vh;
    }
    .email-container {
      background-color: #ffffff;
      overflow: hidden;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    }
    /* Top accent bar - Thicker */
    .accent-bar {
      height: 10px;
      background: linear-gradient(to right, #1a1a1a 0%, #4a4a4a 50%, #1a1a1a 100%);
    }
    /* Header styles */
    .email-header {
      padding: 32px 48px 20px 48px;
      border-bottom: 1px solid #f0f0f0;
    }
    .logo-container {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .logo-image {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
      border: 2px solid #f0f0f0;
    }
    .logo-text {
      font-size: 22px;
      font-weight: 600;
      letter-spacing: -0.3px;
      color: #1a1a1a;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.3;
    }
    .logo-text span {
      color: #888888;
      font-weight: 300;
    }
    .logo-sub {
      font-size: 10px;
      color: #999999;
      letter-spacing: 2px;
      text-transform: uppercase;
      margin-top: 2px;
      font-weight: 400;
      line-height: 1.4;
    }
    /* Body styles */
    .email-body {
      padding: 36px 48px 32px 48px;
    }
    .email-body h1 {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 12px;
      letter-spacing: -0.3px;
      line-height: 1.5;
      text-align: center;
    }
    .email-body > p {
      font-size: 15px;
      line-height: 1.8;
      color: #555555;
      margin-bottom: 20px;
      text-align: center;
    }
    /* Code section - Flat bold text */
    .code-section {
      text-align: center;
      margin: 28px 0 24px 0;
    }
    .code-section .code-label {
      font-size: 13px;
      color: #888888;
      letter-spacing: 0.5px;
      line-height: 1.6;
      text-align: center;
      margin-bottom: 8px;
    }
    .code-section .code-text {
      font-family: 'Courier New', monospace;
      font-size: 48px;
      font-weight: 900;
      color: #1a1a1a;
      letter-spacing: 2px;
      padding: 12px 0;
      line-height: 1.4;
      user-select: all;
      word-break: break-word;
      text-align: center;
    }
    .code-section .copy-hint {
      font-size: 11px;
      color: #aaaaaa;
      margin-top: 10px;
      text-align: center;
    }
    /* Info and warning boxes */
    .info-box {
      padding: 16px 20px;
      margin: 20px 0;
      background: #f8f8fa;
      border-left: 3px solid #1a1a1a;
      border-radius: 0 4px 4px 0;
    }
    .info-box p {
      font-size: 13px;
      color: #666666;
      margin: 0;
      line-height: 1.7;
      text-align: left;
    }
    .info-box p strong {
      color: #1a1a1a;
      font-weight: 600;
    }
    .warning-box {
      padding: 16px 20px;
      margin: 20px 0;
      background: #fdf6f6;
      border-left: 3px solid #d32f2f;
      border-radius: 0 4px 4px 0;
    }
    .warning-box p {
      font-size: 13px;
      color: #666666;
      margin: 0;
      line-height: 1.7;
      text-align: left;
    }
    .warning-box p strong {
      color: #d32f2f;
      font-weight: 600;
    }
    /* Footer styles */
    .email-footer {
      padding: 32px 48px 28px 48px;
      background: #fafafa;
      border-top: 1px solid #f0f0f0;
    }
    .footer-divider {
      height: 1px;
      background: #e8e8ea;
      margin: 20px 0;
    }
    .footer-social-section {
      text-align: center;
      margin-bottom: 18px;
    }
    .footer-social-section .section-label {
      font-size: 11px;
      color: #999999;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 12px;
      display: block;
      font-weight: 500;
      text-align: center;
    }
    .footer-links {
      text-align: center;
      margin-bottom: 16px;
    }
    .footer-links a {
      font-size: 12px;
      color: #888888;
      text-decoration: none;
      letter-spacing: 0.3px;
      margin: 0 10px;
      line-height: 2;
    }
    .footer-brand {
      text-align: center;
      font-size: 14px;
      font-weight: 500;
      color: #1a1a1a;
      margin-bottom: 4px;
      line-height: 1.6;
    }
    .footer-brand span {
      font-weight: 300;
      color: #888888;
    }
    .footer-copy {
      text-align: center;
      font-size: 11px;
      color: #aaaaaa;
      letter-spacing: 0.3px;
      line-height: 1.8;
    }
    .footer-copy a {
      color: #888888;
      text-decoration: none;
    }
    .footer-copy .divider-dot {
      color: #dddddd;
      margin: 0 4px;
    }
    /* Responsive styles */
    @media (max-width: 600px) {
      .email-wrapper { padding: 0; }
      .email-header { padding: 24px 20px 16px 20px; }
      .email-body { padding: 24px 20px 20px 20px; }
      .email-footer { padding: 24px 20px 20px 20px; }
      .footer-links a { margin: 0 6px; font-size: 11px; }
      .logo-image { width: 40px; height: 40px; }
      .logo-text { font-size: 18px; }
      .code-section .code-text {
        font-size: 36px !important;
        letter-spacing: 1px !important;
      }
    }
    @media (max-width: 400px) {
      .code-section .code-text {
        font-size: 28px !important;
        letter-spacing: 0px !important;
      }
    }
  </style>
</head>
<body>
  <!-- Full-page table wrapper for email client compatibility -->
  <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="height: 100%; min-height: 100vh;">
    <tr>
      <td align="center" valign="top" style="background-color: #f0f0f2; padding: 0;">
        <div class="email-wrapper">
          <div class="email-container">
            <!-- Top accent bar - Thicker (10px) -->
            <div class="accent-bar"></div>

            <!-- Header Section -->
            <div class="email-header">
              <table role="presentation" border="0" cellpadding="0" cellspacing="0">
                <tr>
                  <td valign="middle" style="padding-right: 14px;">
                    <img src="${logoUrl}" alt="WD Hejbul" class="logo-image" width="48" height="48" />
                  </td>
                  <td valign="middle" align="left">
                    <div class="logo-text">WD Hejbul <span>Creative Studio</span></div>
                    <div class="logo-sub">Brand &amp; Product Design</div>
                  </td>
                </tr>
              </table>
            </div>

            <!-- Body Section -->
            <div class="email-body">
              <h1>${displayTitle}</h1>
              <p>${displayDescription}</p>

              <!-- Code Section -->
              <div class="code-section">
                <div class="code-label">Enter this 6-digit code to verify your identity</div>
                <div class="code-text">${code}</div>
                <div class="copy-hint">💡 Select the code above and copy it manually</div>
              </div>

              <!-- Security Information Box -->
              <div class="info-box">
                <p><strong>Security Information:</strong> This code will expire in ${expiresInMinutes} minutes. If you did not request this, please ignore this email and your account will remain secure.</p>
              </div>

              <!-- Warning Box -->
              <div class="warning-box">
                <p><strong>Do not share this code:</strong> For your security, never share this verification code with anyone. WD Hejbul will never ask for your code outside of the login process.</p>
              </div>
            </div>

            <!-- Footer Section -->
            <div class="email-footer">
              <!-- Main Social Section -->
              <div class="footer-social-section">
                <span class="section-label">Connect With Me</span>
                ${buildIconTable(mainSocialIcons)}
              </div>

              <div class="footer-divider"></div>

              <!-- Freelance Platforms Section -->
              <div class="footer-social-section">
                <span class="section-label">Hire Me On</span>
                ${buildFreelanceSection()}
              </div>

              <div class="footer-divider"></div>

              <!-- Footer Navigation Links -->
              <div class="footer-links">
                ${footerLinks
                  .map(link => `<a href="${link.href}">${link.label}</a>`)
                  .join('')}
              </div>

              <!-- Brand -->
              <div class="footer-brand">WD Hejbul <span>Creative Studio</span></div>

              <!-- Copyright -->
              <div class="footer-copy">
                &copy; ${year} WD Hejbul. All rights reserved.
                <br>
                <a href="https://wdhejbul.com/privacy-policy">Privacy Policy</a>
                <span class="divider-dot">&middot;</span>
                <a href="https://wdhejbul.com/terms">Terms &amp; Conditions</a>
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// Export as default for convenience
export default generateEmailHTML;
